/**
 * PO Lines table header: the title, the toolbar (quick search, uploads,
 * column picker) and the status pills.
 *
 * Lifted out of POLinesTable (CR-8681 touched it, so its 500-line limit
 * applies). CR-8681 also made it fit a tablet: at 768px the toolbar and the
 * pills ran ~120px past the viewport, so the whole Purchasing page scrolled
 * sideways. The toolbar now wraps onto further rows, and the pills scroll
 * inside their own strip — every control is still there, nothing is hidden.
 */
import { RefObject } from 'react';
import { CardHeader, CardTitle } from '../../../ui/card';
import { TabsList, TabsTrigger } from '../../../ui/tabs';
import { Package, Search, Eye, EyeOff, GripVertical, Columns } from 'lucide-react';
import { ColumnConfig } from './types';
import type { ImportPromiseDatesMethod } from '../ImportPromiseDatesModal';

interface POLinesTableHeaderProps {
  isSupplier: boolean;
  /** Rows on this page. */
  shownCount: number;
  totalCount: number;
  /** Quick-search term (part / supplier part / PO / supplier). */
  partSearch: string;
  onQuickPartSearch?: (value: string) => void;
  /** CR-7778: a customer chooses which upload; a supplier has one. */
  onImportPromiseDates: (method?: ImportPromiseDatesMethod) => void;
  onImportAcknowledgements?: () => void;
  columns: ColumnConfig[];
  /** Columns a feature switch removes from the table and the picker. */
  gatedColumnIds: Set<string>;
  showColumnDropdown: boolean;
  columnDropdownRef: RefObject<HTMLDivElement | null>;
  onSetShowColumnDropdown: (show: boolean) => void;
  onToggleColumn: (columnId: string) => void;
  onResetColumns: () => void;
  onSelectAllColumns: () => void;
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void;
  /** True while the batched count request is in flight — badges pulse. */
  countsRefreshing: boolean;
  scheduleCollabEnabled: boolean;
  hideExceptions: boolean;
  needsActionCount: number | null;
  otifReviewCount: number | null;
  duePreviousWeeksCount: number | null;
  dueThisWeekCount: number | null;
  dueNextWeekCount: number | null;
  openCount: number | null;
  closedCount: number | null;
  onHoldCount: number | null;
}

/** " (n)" after a pill label, only when there is something to count. */
const countSuffix = (count: number | null) => (count !== null && count > 0 ? ` (${count.toLocaleString()})` : '');

const TOOLBAR_BUTTON = 'px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted whitespace-nowrap';

/**
 * The header of the PO Lines card. Must render inside the card's `<Tabs>`.
 *
 * @param props - See {@link POLinesTableHeaderProps}.
 * @returns The card header with toolbar and status pills.
 */
export function POLinesTableHeader({
  isSupplier, shownCount, totalCount, partSearch, onQuickPartSearch,
  onImportPromiseDates, onImportAcknowledgements,
  columns, gatedColumnIds, showColumnDropdown, columnDropdownRef, onSetShowColumnDropdown,
  onToggleColumn, onResetColumns, onSelectAllColumns, onReorderColumns,
  countsRefreshing, scheduleCollabEnabled, hideExceptions,
  needsActionCount, otifReviewCount, duePreviousWeeksCount, dueThisWeekCount, dueNextWeekCount,
  openCount, closedCount, onHoldCount,
}: POLinesTableHeaderProps) {
  return (
    <CardHeader>
      {/* CR-8681: wraps at tablet width — the toolbar drops below the title,
          then onto as many rows as its controls need. */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="po-lines-header">
        <div>
          <CardTitle>{isSupplier ? 'SO Line Items' : 'PO Line Items'}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Showing {shownCount} of {totalCount.toLocaleString()} line items
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 min-w-0 max-w-full" data-testid="po-lines-toolbar">
          {/* Always-visible quick search — one debounced term matched
              server-side against part number, supplier part reference,
              PO number or supplier name (API param part_search). Same
              control and wording as the Schedule Lines toolbar so the two
              behave alike — CR-7232 added supplier to both together. */}
          {onQuickPartSearch && (
            <div className="relative max-w-full">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground pointer-events-none" />
              <input
                type="text"
                value={partSearch}
                onChange={(e) => onQuickPartSearch(e.target.value)}
                placeholder="Search Part, Supplier Part, PO or Supplier"
                // border-foreground reads as black in light mode and stays
                // visible in dark, where a hardcoded black would vanish.
                // w-80: the placeholder names four fields since CR-7232 and
                // clips below it. max-w-full: never wider than the toolbar.
                className="pl-8 pr-2 py-1.5 text-sm border-2 border-foreground rounded-lg w-80 max-w-full bg-background"
                aria-label="Quick search by part number, supplier part number, PO number or supplier name"
              />
            </div>
          )}
          {/* CR-6348 rework: Both customer and supplier views now expose
              Filters + Export in the filter panel header (persistent header
              pattern), not in this toolbar. */}
          {/* EXPEDITING-3: each side uploads its own date. CR-7778: the
              customer has two separate uploads, each with its own template:
              the supplier's updates on the supplier's behalf, and its own.
              The supplier's upload uses the same supplier updates template
              (dates, price and quantity), so it carries the same name. */}
          {isSupplier ? (
            <button onClick={() => onImportPromiseDates()} className={TOOLBAR_BUTTON} title="Upload your promise dates, prices and quantities from CSV/Excel" data-testid="upload-supplier-updates">
              <Package className="h-3 w-3" />Upload supplier updates
            </button>
          ) : (
            <>
              <button onClick={() => onImportPromiseDates('supplier_updates')} className={TOOLBAR_BUTTON} title="Upload the supplier's promise dates, prices and quantities on the supplier's behalf" data-testid="upload-supplier-updates">
                <Package className="h-3 w-3" />Upload supplier updates
              </button>
              <button onClick={() => onImportPromiseDates('customer_updates')} className={TOOLBAR_BUTTON} title="Upload your own customer required dates" data-testid="upload-customer-updates">
                <Package className="h-3 w-3" />Upload customer updates
              </button>
            </>
          )}

          {/* CR-6679: Order Acknowledgements import - suppliers and customers */}
          {onImportAcknowledgements && (
            <button onClick={onImportAcknowledgements} className={TOOLBAR_BUTTON} title="Bulk acknowledge PO lines via Excel/CSV upload">
              <Package className="h-3 w-3" />Import Acknowledgements
            </button>
          )}

          {/* Column Visibility Dropdown */}
          <div className="relative" ref={columnDropdownRef}>
            <button onClick={() => onSetShowColumnDropdown(!showColumnDropdown)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showColumnDropdown ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              <Columns className="h-3 w-3" />Columns
            </button>
            {showColumnDropdown && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                <div className="p-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Manage Columns</span>
                  <div className="flex items-center gap-2">
                    <button onClick={onSelectAllColumns} className="text-xs text-primary hover:underline">Select All</button>
                    <span className="text-xs text-muted-foreground">|</span>
                    <button onClick={onResetColumns} className="text-xs text-primary hover:underline">Reset</button>
                  </div>
                </div>
                <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder - Click to show/hide</div>
                <div className="p-2 space-y-1">
                  {columns.map((col, index) => (
                    gatedColumnIds.has(col.id) ? null : (
                    <div
                      key={col.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', index.toString()); e.currentTarget.classList.add('opacity-50'); }}
                      onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); }}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('bg-primary/10'); }}
                      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10); if (dragIndex !== index) onReorderColumns(dragIndex, index); }}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                      <input type="checkbox" checked={col.visible} onChange={() => onToggleColumn(col.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                      <span className="text-sm flex-1">{col.label}</span>
                      {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Counts stay visible but pulse while the batched request is in
          flight, so a stale number is recognisable as refreshing.
          CR-8681: max-w-full + overflow-x-auto — on a narrow screen the pills
          scroll sideways inside this strip instead of widening the page. */}
      <TabsList data-tour="quick-filters" data-testid="po-lines-status-tabs" className={`mt-3 justify-start max-w-full overflow-x-auto overflow-y-hidden ${countsRefreshing ? 'animate-pulse' : ''}`}>
        <TabsTrigger value="all">All Lines</TabsTrigger>
        {/* CR-5884: Exceptions tab respects supplier-side hide_exceptions toggle. */}
        {!scheduleCollabEnabled && !hideExceptions && (
          <TabsTrigger value="needs_action" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
            Exceptions{needsActionCount !== null && needsActionCount > 0 ? ` (${needsActionCount})` : ''}
          </TabsTrigger>
        )}
        {/* CR-7418: OTIF Review — the queue of delivered lines whose OTIF
            verdict a buyer must settle. Legacy has always carried this as a
            sub-tab of the PO line grid; Helix built it on Supplier
            Performance by mistake, away from the actions it drives.
            A supplier sees it too, read only (CR-6348, Aaron 5 Oct). Red on
            Aaron's call (19 Sep) — the first build used violet to keep it
            away from the urgency colours, and he wants this queue reading
            as the one that needs attention. */}
        {/* CR-6518 rework: a tenant that collaborates at SCHEDULE grain
            settles its OTIF verdicts on the schedule, so there is no
            PO-line queue to work — hidden like Exceptions and On Hold
            above and below, which that switch already removes. This tab
            is the one surface the backend's can_* flags cannot close:
            the trigger renders before any row is read. */}
        {!scheduleCollabEnabled && (
          <TabsTrigger value="otif_review" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            OTIF Review{countSuffix(otifReviewCount)}
          </TabsTrigger>
        )}
        {/* Due windows mirror the Schedule Lines tabs of the same names —
            same Monday-based weeks, same colours — so a planner reads both
            surfaces the same way. Forecasted is deliberately not carried
            over: PO lines have no forecast concept. */}
        <TabsTrigger value="due_previous_weeks" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white">
          Due From Previous Weeks{countSuffix(duePreviousWeeksCount)}
        </TabsTrigger>
        <TabsTrigger value="due_this_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
          Due This Week{countSuffix(dueThisWeekCount)}
        </TabsTrigger>
        <TabsTrigger value="due_next_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
          Due Next Week{countSuffix(dueNextWeekCount)}
        </TabsTrigger>
        <TabsTrigger value="open">Open{countSuffix(openCount)}</TabsTrigger>
        <TabsTrigger value="closed">Closed{countSuffix(closedCount)}</TabsTrigger>
        {!scheduleCollabEnabled && (
          <TabsTrigger value="on_hold" data-tour="on-hold-filter">On Hold{countSuffix(onHoldCount)}</TabsTrigger>
        )}
      </TabsList>
    </CardHeader>
  );
}
